import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from '../firebase';
import { signInWithEmailAndPassword, GoogleAuthProvider, signInWithPopup } from 'firebase/auth';
import { authAPI } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';

const getGoogleSignInErrorMessage = (err) => {
  const code = err?.code;

  if (code === 'auth/popup-closed-by-user') {
    return '';
  }
  if (code === 'auth/popup-blocked') {
    return 'Popup was blocked by your browser. Allow popups for this site and try again.';
  }
  if (code === 'auth/cancelled-popup-request') {
    return 'Another sign-in popup is already open. Close it and try again.';
  }
  if (code === 'auth/unauthorized-domain') {
    return 'This domain is not authorized in Firebase. Add localhost to Firebase Authentication > Authorized domains.';
  }
  if (code === 'auth/operation-not-allowed') {
    return 'Google sign-in is not enabled in Firebase. Enable Google provider in Firebase Authentication > Sign-in method.';
  }
  if (code === 'auth/invalid-api-key') {
    return 'Invalid Firebase API key. Check REACT_APP_FIREBASE_API_KEY in frontend environment configuration.';
  }
  if (code === 'auth/network-request-failed') {
    return 'Network error during Google sign-in. Check your internet connection and retry.';
  }
  if (code === 'auth/configuration-not-found') {
    return 'Firebase Auth configuration not found. Verify Firebase app config values and Google provider setup.';
  }

  if (err?.response?.data?.detail) {
    return `Google sign-in succeeded, but backend sync failed: ${err.response.data.detail}`;
  }

  return `Google sign-in failed: ${err?.message || 'Unknown error'}`;
};

/**
 * Production Login Page — Firebase Authentication
 * Supports email/password and Google sign-in.
 * Firebase handles auth, backend /auth/sync verifies the token server-side.
 */
const Login = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  
  // State management
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Mobile responsiveness
  const [isMobile, setIsMobile] = React.useState(typeof window !== 'undefined' && window.innerWidth <= 768);
  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Clear error after 5 seconds
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(''), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  /**
   * Shared post-auth flow: verify token with backend, store credentials, redirect
   */
  const syncAndRedirect = async (firebaseUser) => {
    let role = 'student';
    const userEmail = (firebaseUser.email || '').toLowerCase();
    let fullName = firebaseUser.displayName || userEmail.split('@')[0] || 'User';
    let userId = firebaseUser.uid;
    let token = '';

    const adminEmails = [
      'dr.sabitha@arivunest.com',
      'nandhakumar@arivunest.com',
      'kanirathinamfoundation@gmail.com',
      'smnk2006@gmail.com'
    ];
    if (adminEmails.includes(userEmail)) {
      role = 'admin';
    }

    try {
      const idToken = await firebaseUser.getIdToken();
      token = idToken;
      const syncRes = await authAPI.syncUser({ firebase_token: idToken });
      if (syncRes && syncRes.data) {
        token = syncRes.data.access_token || idToken;
        role = syncRes.data.role || role;
        fullName = syncRes.data.full_name || fullName;
        userId = syncRes.data.user_id || userId;
      }
    } catch (backendErr) {
      console.warn('Backend syncUser unavailable, using resilient Firebase auth session:', backendErr);
    }

    localStorage.setItem('token', token);
    localStorage.setItem('user', JSON.stringify({
      uid: userId,
      email: userEmail,
      name: fullName,
      role: role
    }));

    if (role === 'instructor') navigate('/instructor-dashboard');
    else if (role === 'admin') navigate('/admin-dashboard');
    else navigate('/');
  };

  /**
   * Email/password login via Firebase
   */
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setError('Please enter email and password.');
      return;
    }

    setError('');
    setLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      console.log('Firebase auth successful, syncing with backend...');
      await syncAndRedirect(userCredential.user);
    } catch (err) {
      console.error('Login error:', err);
      console.error('Error code:', err.code);
      console.error('Error message:', err.message);
      console.error('Error response:', err.response?.data);
      if (err.code === 'auth/user-not-found' || err.code === 'auth/invalid-credential') {
        setError('Invalid email or password.');
      } else if (err.code === 'auth/wrong-password') {
        setError('Invalid password. Please try again.');
      } else if (err.code === 'auth/too-many-requests') {
        setError('Too many failed attempts. Please try again later.');
      } else if (err.code === 'auth/user-disabled') {
        setError('This account has been disabled. Contact support.');
      } else if (err.response?.data?.detail) {
        setError(err.response.data.detail);
      } else {
        setError(`Login failed: ${err.message || 'Please try again.'}`);
      }
    }

    setLoading(false);
  };

  /**
   * Google sign-in via Firebase popup
   */
  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);

    try {
      const provider = new GoogleAuthProvider();
      const result = await signInWithPopup(auth, provider);
      await syncAndRedirect(result.user);
    } catch (err) {
      console.error('Google login error:', err);
      console.error('Google login error code:', err?.code);
      console.error('Google login backend detail:', err?.response?.data);

      const friendlyMessage = getGoogleSignInErrorMessage(err);
      if (!friendlyMessage) {
        setLoading(false);
        return;
      }
      setError(friendlyMessage);
    }

    setLoading(false);
  };

  return (
    <div style={styles.pageContainer}>
      {/* Background gradient */}
      <div style={styles.background} />

      {/* Main container */}
      <div style={{...styles.container, gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr'}}>
        {/* Left: Branding (hidden on mobile) */}
        {!isMobile && (
        <div style={styles.brandSection}>
          <div style={styles.brandContent}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' }}>
              <img src="/images/logos/arivu-nest-logo.png" alt="Arivu Nest" style={{ height: '52px', width: 'auto' }} />
              <div>
                <h1 style={{ ...styles.brandTitle, margin: 0, fontSize: '32px' }}>ARIVU NEST</h1>
                <div style={{ fontSize: '11px', color: '#a7f3d0', fontWeight: '800', letterSpacing: '1px', textTransform: 'uppercase' }}>
                  Research & Development
                </div>
              </div>
            </div>
            <p style={styles.brandSubtitle}>{t('masterEnglishSpeaking')}</p>

            <div style={styles.features}>
              <div style={styles.feature}>
                <span style={styles.featureIcon}>🎙️</span>
                <p>{t('recordPracticeSpeaking')}</p>
              </div>
              <div style={styles.feature}>
                <span style={styles.featureIcon}>⚡</span>
                <p>{t('aiPoweredAnalysis')}</p>
              </div>
              <div style={styles.feature}>
                <span style={styles.featureIcon}>📊</span>
                <p>{t('trackYourProgress')}</p>
              </div>
              <div style={styles.feature}>
                <span style={styles.featureIcon}>🎓</span>
                <p>{t('expertCuratedLessons')}</p>
              </div>
            </div>
          </div>
        </div>
        )}

        {/* Right: Login Form */}
        <div style={styles.formSection}>
          <div style={styles.formCard}>
            <h2 style={styles.formTitle}>{t('welcomeBack')}</h2>
            <p style={styles.formSubtitle}>{t('signInToContinue')}</p>

            {error && (
              <div style={styles.errorBanner}>
                <span style={styles.errorIcon}></span>
                {error}
              </div>
            )}

            <form onSubmit={handleLogin} style={styles.form}>
              {/* Email Input */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('emailAddress')}</label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={t('enterYourEmail')}
                  style={styles.input}
                  disabled={loading}
                  autoComplete="email"
                />
              </div>

              {/* Password Input */}
              <div style={styles.inputGroup}>
                <label style={styles.label}>{t('password')}</label>
                <div style={styles.passwordWrapper}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={t('enterYourPassword')}
                    style={styles.input}
                    disabled={loading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={styles.eyeButton}
                    title={showPassword ? t('hidePassword') : t('showPassword')}
                  >
                    {showPassword ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>

              {/* Forgot Password Link */}
              <div style={styles.forgotPassword}>
                <span 
                  onClick={() => navigate('/forgot-password')}
                  style={styles.forgotLink}
                >
                  {t('forgotPassword')}
                </span>
              </div>

              {/* Login Button */}
              <button
                type="submit"
                style={{
                  ...styles.submitButton,
                  opacity: loading ? 0.7 : 1,
                  cursor: loading ? 'not-allowed' : 'pointer'
                }}
                disabled={loading}
              >
                {loading ? t('signingIn') : t('signIn')}
              </button>
            </form>

            {/* Divider */}
            <div style={styles.divider}>
              <span style={styles.dividerLine} />
              <span style={styles.dividerText}>{t('or')}</span>
              <span style={styles.dividerLine} />
            </div>

            {/* Google Sign-In */}
            <button
              type="button"
              onClick={handleGoogleLogin}
              style={{
                ...styles.googleButton,
                opacity: loading ? 0.7 : 1,
                cursor: loading ? 'not-allowed' : 'pointer'
              }}
              disabled={loading}
            >
              <svg style={{ width: 20, height: 20, marginRight: 10 }} viewBox="0 0 48 48">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              {t('continueWithGoogle')}
            </button>

            {/* SignUp Link */}
            <div style={styles.signupSection}>
              <p style={styles.signupText}>
                {t('dontHaveAccount')}{' '}
                <span
                  onClick={() => navigate('/signup')}
                  style={styles.signupLink}
                >
                  {t('createAccount')}
                </span>
              </p>
            </div>

            {/* Security Footer */}
            <div style={styles.footer}>
              <p style={styles.footerText}>
                 {t('secureLoginProtected')}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============= STYLES =============
const styles = {
  pageContainer: {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
    overflow: 'hidden'
  },
  background: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    zIndex: -1
  },
  container: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '20px',
    maxWidth: '1200px',
    margin: '0 auto',
    padding: '40px 20px',
    alignItems: 'center',
    width: '100%'
  },
  brandSection: {
    color: '#fff',
    padding: '40px 20px'
  },
  brandContent: {
    maxWidth: '500px'
  },
  brandTitle: {
    fontSize: 'clamp(28px, 5vw, 48px)',
    fontWeight: '700',
    marginBottom: '16px',
    lineHeight: '1.2'
  },
  brandSubtitle: {
    fontSize: '20px',
    marginBottom: '40px',
    opacity: 0.95,
    lineHeight: '1.5'
  },
  features: {
    display: 'grid',
    gap: '20px',
    marginTop: '40px'
  },
  feature: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    fontSize: '16px'
  },
  featureIcon: {
    fontSize: 'clamp(24px, 4vw, 32px)'
  },
  formSection: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  },
  formCard: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: 'clamp(20px, 4vw, 40px)',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    width: '100%',
    maxWidth: '420px'
  },
  formTitle: {
    fontSize: '28px',
    fontWeight: '700',
    marginBottom: '8px',
    color: '#333'
  },
  formSubtitle: {
    fontSize: '14px',
    color: '#666',
    marginBottom: '24px'
  },
  errorBanner: {
    backgroundColor: '#fff5f5',
    border: '1px solid #fc8181',
    color: '#c53030',
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
    fontWeight: '500',
    display: 'flex',
    alignItems: 'center',
    gap: '8px'
  },
  errorIcon: {
    fontSize: '16px'
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px'
  },
  inputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px'
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#333'
  },
  input: {
    padding: '14px 16px',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    backgroundColor: '#fafafa',
    width: '100%',
    boxSizing: 'border-box'
  },
  passwordWrapper: {
    position: 'relative',
    display: 'flex',
    alignItems: 'center'
  },
  eyeButton: {
    position: 'absolute',
    right: '12px',
    top: '50%',
    transform: 'translateY(-50%)',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '18px',
    padding: '4px'
  },
  forgotPassword: {
    textAlign: 'right',
    marginTop: '-8px'
  },
  forgotLink: {
    fontSize: '13px',
    color: '#667eea',
    cursor: 'pointer',
    textDecoration: 'none'
  },
  submitButton: {
    padding: '14px 20px',
    backgroundColor: '#27b05b',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    marginTop: '8px',
    transition: 'background-color 0.3s'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '20px 0'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    backgroundColor: '#e0e0e0'
  },
  dividerText: {
    fontSize: '13px',
    color: '#999',
    fontWeight: '500'
  },
  googleButton: {
    width: '100%',
    padding: '12px 20px',
    backgroundColor: '#fff',
    color: '#333',
    border: '2px solid #e0e0e0',
    borderRadius: '8px',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'border-color 0.2s, box-shadow 0.2s',
    fontFamily: 'inherit'
  },
  signupSection: {
    textAlign: 'center',
    marginTop: '24px',
    paddingTop: '24px',
    borderTop: '1px solid #e0e0e0'
  },
  signupText: {
    fontSize: '14px',
    color: '#666'
  },
  signupLink: {
    color: '#27b05b',
    fontWeight: '600',
    cursor: 'pointer',
    textDecoration: 'underline'
  },
  footer: {
    marginTop: '20px',
    textAlign: 'center'
  },
  footerText: {
    fontSize: '12px',
    color: '#999'
  }
};

export default Login;
